import React, { useEffect, useMemo, useState } from 'react';
import { FeedbackTemplate } from '@bb/domain/dist/types/Feedback';
import { RadioChangeEvent } from 'antd';
import { Button, RadioGroup, RadioButton } from 'components';
import styled from 'styled-components';

type QuestionCustomizerProps = {
  question: FeedbackTemplate;
  getQuestion: (question: FeedbackTemplate) => void;
  isEdit?: boolean;
  ModalService: {
    closeQuestionEditModal: (
      question?: FeedbackTemplate,
    ) => Promise<FeedbackTemplate>;
  };
};

const QuestionCustomizer: React.FC<QuestionCustomizerProps> = ({
  question,
  getQuestion,
  isEdit,
  ModalService,
}) => {
  const [createdQuestion, setCreatedQuestion] = useState<FeedbackTemplate>({
    type: 'text',
    label: '',
  });
  const [createdOption, setCreatedOption] = useState<string>('');

  useEffect(() => {
    if (question) {
      setCreatedQuestion(question);
    }
  }, []);

  const onChangeQuestionType = (event: RadioChangeEvent) => {
    const type = event.target.value;
    setCreatedQuestion({
      type,
      label: '',
      ...(type !== 'text' && { options: [] }),
    });
  };

  const changeQuestionLabelHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const label = event.target.value || '';
    setCreatedQuestion(prevState => ({
      ...prevState,
      label,
    }));
  };

  const changeOptionHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreatedOption(event.target.value);
  };

  const addOption = () => {
    if (createdOption) {
      setCreatedQuestion(prevState => ({
        ...prevState,
        options: prevState.options
          ? [...prevState.options, createdOption]
          : [createdOption],
      }));
      setCreatedOption('');
    }
  };

  const removeOption = (index: number) => {
    const updatedArray = createdQuestion.options;
    updatedArray?.splice(index, 1);
    setCreatedQuestion(prevState => ({
      ...prevState,
      options: updatedArray,
    }));
  };

  const resetQuestion = () => {
    setCreatedQuestion({ type: 'text', label: '' });
  };

  const sendQuestion = () => {
    getQuestion(createdQuestion);
    resetQuestion();
  };

  const saveEdit = () => {
    ModalService.closeQuestionEditModal(createdQuestion);
  };

  const addQuestionDisabled = useMemo(
    () =>
      !createdQuestion.label ||
      (createdQuestion.type !== 'text' &&
        createdQuestion.options?.length === 0),

    [createdQuestion],
  );

  return (
    <>
      <div className="row">
        <div className="col-lg-12 question-customizer">
          <div className="col-lg-6 question-form">
            <div>
              <label data-cy="question-type-label">
                Question Type: {isEdit && question.type}
              </label>
            </div>
            {!isEdit && (
              <RadioGroup
                defaultValue="text"
                buttonStyle="solid"
                onChange={onChangeQuestionType}
                value={createdQuestion.type}
              >
                <RadioButton value="text">
                  <span data-cy="question-type-area">Type Area</span>
                </RadioButton>
                <RadioButton value="checkbox">
                  <span data-cy="question-checkbox">Checkbox</span>
                </RadioButton>
                <RadioButton value="radio">
                  <span data-cy="question-radio">Radio</span>
                </RadioButton>
                <RadioButton value="select">
                  <span data-cy="question-dropdown">Dropdown</span>
                </RadioButton>
              </RadioGroup>
            )}
            <StyledInputContainer>
              <label data-cy="question-your-question-label">
                Your Question:
              </label>
              <input
                className="form-control"
                placeholder="Enter your feedback question here..."
                onChange={changeQuestionLabelHandler}
                value={createdQuestion.label}
                type="text"
                data-cy="input-enter-question"
              ></input>
              <br />
              {createdQuestion.type !== 'text' && (
                <label data-cy="question-options-label">Options:</label>
              )}
              {createdQuestion.type === 'select' && (
                <div>
                  {createdQuestion?.options?.map((option, index) => (
                      <div key={index}>
                        <label className="control-label">
                          <input type="radio" name={createdQuestion.label} />
                          {option}
                          <i
                            onClick={() => removeOption(index)}
                            className="fa fa-times-circle pull-right text-danger option-remove"
                            data-cy={`select-remove-${index}`}
                          ></i>
                        </label>
                      </div>
                    ))}
                  <select className="form-control">
                    {createdQuestion?.options?.map((option, index) => <option key={index}>{option}</option>)}
                  </select>
                </div>
              )}
              {createdQuestion.type === 'radio' && (
                <div>
                  {createdQuestion?.options?.map((option, index) => (
                      <div key={index}>
                        <StyledCheckBoxAndRadioContainer>
                          <StyledInput
                            type="radio"
                            name={createdQuestion.label}
                          />
                          <span>{option}</span>
                          <StyledIcon
                            onClick={() => removeOption(index)}
                            className="fa fa-times-circle pull-right text-danger option-remove"
                            data-cy={`radio-remove-${index}`}
                          />
                        </StyledCheckBoxAndRadioContainer>
                      </div>
                    ))}
                </div>
              )}
              {createdQuestion.type === 'checkbox' && (
                <div>
                  {createdQuestion?.options?.map((option, index) => (
                      <div key={index}>
                        <StyledCheckBoxAndRadioContainer>
                          <StyledInput type="checkbox" name={option} />
                          <span>{option}</span>
                          <StyledIcon
                            onClick={() => removeOption(index)}
                            className="fa fa-times-circle pull-right text-danger option-remove"
                            data-cy={`checkbox-remove-${index}`}
                          />
                        </StyledCheckBoxAndRadioContainer>
                      </div>
                    ))}
                </div>
              )}
              {createdQuestion.type !== 'text' && (
                <StyledOptionInputContainer>
                  <input
                    className="form-control option-input"
                    placeholder="Enter options for your question then press add."
                    data-cy="question-input-text"
                    value={createdOption}
                    onChange={changeOptionHandler}
                  />
                  <Button
                    onClick={addOption}
                    shape="round"
                    size="small"
                    data-cy="question-add-text-input"
                  >
                    <StyledAddOptionButton>
                      Add <i className="fa fa-plus-circle"></i>
                    </StyledAddOptionButton>
                  </Button>
                </StyledOptionInputContainer>
              )}
            </StyledInputContainer>
            {!isEdit && (
              <Button
                onClick={sendQuestion}
                data-cy="question-add-button"
                type="primary"
                // Needed to overwrite inherited grey
                style={{
                  color: addQuestionDisabled ? 'rgba(0, 0, 0, 0.25)' : '#fff',
                }}
                block
                disabled={addQuestionDisabled}
              >
                Add question
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* These are the modal buttons. Needed to handle the state from children react component */}
      {isEdit && (
        <div className="modal-footer">
          <button
            className="btn btn-primary pull-right margin-right"
            type="button"
            onClick={saveEdit}
          >
            Save
          </button>
          <button
            className="btn btn-default pull-right margin-right"
            type="button"
            onClick={() => ModalService.closeQuestionEditModal()}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
};

const StyledInputContainer = styled.div`
  margin: 10px 0;
`;

const StyledOptionInputContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledAddOptionButton = styled.span`
  margin: 0 14px;
`;

const StyledCheckBoxAndRadioContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
`;

const StyledInput = styled.input`
  cursor: pointer;
  margin: 0px !important;
`;

const StyledIcon = styled.i`
  cursor: pointer;
`;

export default QuestionCustomizer;
